<template>
  <div v-if="productId" class="product-chart">
    <apexchart
      v-if="!isLoading"
      :width="width"
      :height="height"
      :type="chartType"
      :options="chartOptions"
      :series="series"
    >
    </apexchart>
    <div v-if="isLoading" class="loader-container">
      <span class="loader"></span>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ProductPriceChart',
  props: {
    productId: {
      type: Number,
      required: true
    },
    width: {
      type: String,
      default: '100%',
      required: false
    },
    height: {
      type: String,
      default: '100%',
      required: false
    },
    forceUpdate: {
      type: Boolean,
      default: false,
      required: false
    },
    chartType: {
      type: String,
      default: 'line',
      required: false
    }
  },
  data () {
    return {
      isLoading: true,
      alreadyFetched: false,
      chartOptions: {
        chart: {
          id: 'productPriceChart',
          type: this.chartType,
          toolbar: {
            show: true,
            offsetX: 0,
            offsetY: 0,
            tools: {
              download: false,
              selection: false,
              zoom: false,
              zoomin: true,
              zoomout: true,
              pan: false,
              customIcons: []
            }
          }
        },
        dataLabels: {
          enabled: false
        },
        title: {
          text: this.t('product.price.chart.title'),
          align: 'left',
          style: {
            fontSize: '20px'
          }
        },
        stroke: {
          curve: 'smooth',
        },
        colors: ['#00897b'],
        labels: [],
        xaxis: {
          type: 'datetime'
        },
        responsive: []
      },
      series: [{
        name: 'Price',
        data: []
      }],
      prices: [],
      referencePrice: 0
    }
  },
  created () {
    if (this.forceUpdate) {
      this.updateChart()
    }
    this.$parent.$on('updateChart', () => {
      this.updateChart()
    })
  },
  computed: {
    ...mapGetters(
      'productChart',
      [
        'getProductChartIsLoading',
        'getProductChartOptions',
        'getProductChartSeries',
        'getProductChartPrices',
        'getProductChartReferencePrice',
        'getProductChartAlreadyFetched',
        'getProductChartInitialData',
        'getProductExistingChart'
      ]),
    chartInitialData () {
      return this.getProductChartInitialData(this.productId)
    },
    existingChart () {
      return this.getProductExistingChart(this.productId)
    }
  },
  methods: {
    ...mapActions(
      'productChart',
      [
        'setProductChartIsLoading',
        'setProductChartOptions',
        'setProductChartSeries',
        'setProductChartPrices',
        'setProductChartReferencePrice'
      ]),
    async handleEmptyData () {
      this.setProductChartSeries({
        productId: this.productId,
        series: [{
          name: 'Price',
          data: []
        }]
      })
      this.isLoading = false
    },
    async processData (data) {
      this.setProductChartPrices({ productId: this.productId, prices: data.prices })
      this.setProductChartReferencePrice({ productId: this.productId, referencePrice: data.referencePrice })

      const seriesData = data.prices.map(price => Number(price.price))
      this.setProductChartSeries({
        productId: this.productId,
        series: [{
          name: 'Price',
          data: seriesData
        }]
      })

      const labels = data.prices.map(price => price.date)
      this.setProductChartOptions({
        productId: this.productId,
        options: {
          ...this.chartOptions,
          ...{
            labels: labels
          }
        }
      })

      this.chartOptions = this.getProductChartOptions(this.productId)
      this.series = this.getProductChartSeries(this.productId)
      this.prices = this.getProductChartPrices(this.productId)
      this.referencePrice = this.getProductChartReferencePrice(this.productId)
      this.alreadyFetched = this.getProductChartAlreadyFetched(this.productId)
      this.isLoading = false
    },
    async updateChart () {
      if (this.alreadyFetched) {
        return
      }

      if (this.chartInitialData) {
        this.setFromInitialData()
        return
      }

      try {
        this.setProductChartIsLoading({ productId: this.productId, isLoading: true })
        const apiUrl = `/api/priceTracking/productPrices/${this.productId}`
        const response = await fetch(apiUrl)

        if (!response.ok) {
          throw new Error('Network response was not ok')
        }

        const data = await response.json()

        if (data.length === 0) {
          await this.handleEmptyData()
        } else {
          await this.processData(data)
        }
      } catch (error) {
        console.error('Error fetching data:', error)
      }
    },
    setFromInitialData () {
      this.setProductChartPrices({ productId: this.productId, prices: this.existingChart.prices })
      this.setProductChartReferencePrice({ productId: this.productId, referencePrice: this.existingChart.referencePrice })
      const seriesData = this.existingChart.prices.map(price => Number(price.price))
      this.setProductChartSeries({
        productId: this.productId,
        series: [{
          name: 'Price',
          data: seriesData
        }]
      })

      const labels = this.existingChart.prices.map(price => price.date)
      this.setProductChartOptions({
        productId: this.productId,
        options: {
          ...this.chartOptions,
          ...{
            labels: labels
          }
        }
      })
      this.setProductChartIsLoading({ productId: this.productId, isLoading: false })

      this.chartOptions = this.getProductChartOptions(this.productId)
      this.series = this.getProductChartSeries(this.productId)
      this.prices = this.getProductChartPrices(this.productId)
      this.referencePrice = this.getProductChartReferencePrice(this.productId)
      this.alreadyFetched = this.getProductChartAlreadyFetched(this.productId)
      this.isLoading = this.getProductChartIsLoading(this.productId)
    }
  }
}
</script>

<style lang="scss" scoped>
.loader-container {
  display: grid;
  justify-items: center;
  align-items: center;
}
.loader {
  position: relative;
  display: grid;
  border: 6px solid #f3f3f3;
  border-radius: 50%;
  border-top: 6px solid #3498db;
  width: 26px;
  height: 26px;
  -webkit-animation: spin 2s linear infinite;
  animation: spin 2s linear infinite;
  justify-items: center;
  align-items: center;
}

/* Safari */
@-webkit-keyframes spin {
  0% { -webkit-transform: rotate(0deg); }
  100% { -webkit-transform: rotate(360deg); }
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
